import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Tabs, Tab, styled } from '@mui/material';
import Select, { SingleValue } from 'react-select';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import SelectObjectDropdown from './Dropdown/SelectObjectDropdown';
import objectNavigationData, { ObjectNavigationData } from './ObjectNavigationData';

import useScreenSize from 'hooks/Screen/useScreenSize';
import './ObjectNavPage.scss';
import { getObjectHeaderData } from 'api/objectHeader/getObjectHeaderData';
import DevicesStatusChart from 'components/Charts/DevicesStatusChart';

const CustomTabs = styled(Tabs)({
  height: '100%',
  '& .MuiTabs-indicator': {
    display: 'none', // Disable the bottom line
  },
  '& .MuiTabs-scroller': { verticalAlign: 'middle' },

  '& .MuiTab-root': {
    minHeight: 0,
    minWidth: 'auto',

    textTransform: 'none',
    marginRight: '10px',
    color: '#6a37d4',
    fontSize: '16px',
    border: '2px solid #6a37d4',
    borderRadius: '5px',

    '&.Mui-selected': {
      color: '#6a37d4',
      borderRadius: '1rem',
      backgroundColor: 'rgba(111, 0, 133, 0.1)',
    },
  },
  '& .MuiTabs-scrollButtons.Mui-disabled': {
    display: 'none',
  },
});

const ObjectNavPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const { t } = useTranslation();
  const { isNotTablet } = useScreenSize();
  const { data: headerData } = useQuery(['headerData', slug], () => getObjectHeaderData(slug || ''), {
    enabled: !!slug,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [value, setValue] = useState(`/${location.pathname.split('/')[1]}/${location.pathname.split('/')[2]}`);
  const isTabValid = objectNavigationData.some(item => item.path === value);
  useEffect(() => {
    setValue(`/${location.pathname.split('/')[1]}/${location.pathname.split('/')[2]}`);
  }, [location.pathname]);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: string,
    toDjango: boolean = false,
    toPlan: boolean = false,
  ) => {
    if (toDjango && !toPlan) {
      window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}${newValue}/${slug}`;
    } else if (toPlan) {
      if (headerData?.header_plan) {
        window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}${newValue}/${slug}/${headerData.header_plan}`;
      } else if (headerData?.header_plan === null) {
        window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}/floor-plan/add/${slug}`;
      } else {
        console.error('headerData or header_plan is undefined');
      }
    } else {
      setValue(newValue);
      navigate(`${newValue}/${slug}`);
    }
  };

  const handleDropdownChange = (newValue: SingleValue<ObjectNavigationData>) => {
    if (!newValue) return;

    const selectedValue = newValue.path;
    const selectedNavItem = objectNavigationData.find(item => item.path === selectedValue);

    if (selectedNavItem?.toDjango && !selectedNavItem?.toPlan) {
      window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}${selectedValue}/${slug}`;
    } else if (selectedNavItem?.toPlan) {
      if (headerData?.header_plan) {
        window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}${selectedValue}/${slug}/${headerData.header_plan}`;
      } else {
        window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}floor-plan/add/${slug}`;
      }
    } else {
      setValue(selectedValue);
      navigate(`${selectedValue}/${slug}`);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="page-title-box d-flex flex-column-reverse flex-xl-row gap-xl-2 align-items-center justify-content-between">
          <div className="d-flex flex-xl-row flex-md-row gap-md-2 flex-column justify-content-between">
            <div className="d-flex justify-content-center my-2 sm-my-0">
              <SelectObjectDropdown dropDownData={headerData?.objects_select_dropdown || {}} location={location} />
            </div>
            <div className="page-title-left search-box d-flex align-items-center">
              <div
                className="d-flex flex-wrap flex-md-nowrap align-items-center mx-xl-2 flex-grow-1 text-nowrap"
                style={{ minWidth: 0 }}
              >
                <i className="ri-building-line align-bottom me-1" />
                <span className="text-truncate">{headerData?.serviceprovider_addres}</span>
              </div>
            </div>
          </div>
          <div className="d-lg-flex d-none flex-lg-row mt-2  align-items-start ">
            <div className="d-flex align-items-center gap-2">
              {headerData?.header_fast_data && (
                <DevicesStatusChart
                  mctCount={headerData.header_fast_data.mct_count}
                  mctActive={headerData.header_fast_data.mct_active}
                  sbsCount={headerData.header_fast_data.srbm_count}
                  sbsActive={headerData.header_fast_data.srbm_active}
                  srstCount={headerData.header_fast_data.srst_count}
                  srstActive={headerData.header_fast_data.srst_active}
                  smstCount={headerData.header_fast_data.smst_count}
                  smstActive={headerData.header_fast_data.smst_active}
                  isFullInfo={false}
                  isFirstInfo
                />
              )}
            </div>
            <div
              className="d-flex flex-lg-column   align-items-start justify-content-center gap-2 mt-2 mx-2 pt-1"
              style={{ width: '130px' }}
            >
              {headerData?.header_fast_data && headerData.header_fast_data.mct_count > 0 && (
                <>
                  <span className="d-none d-xxl-block badge rounded-3 bg-success mt-1">
                    Mice captured: {headerData.header_fast_data.mct_mice_caught}
                  </span>
                  <span className="d-flex d-xxl-none badge rounded-3 bg-success mt-1">
                    MICE: {headerData.header_fast_data.mct_mice_caught}
                  </span>
                </>
              )}
              {headerData?.header_fast_data && headerData.header_fast_data.srbm_count > 0 && (
                <span className="badge rounded-3 bg-success mt-1">
                  Bait consumed: {headerData.header_fast_data.bait_eaten}g
                </span>
              )}
            </div>
          </div>
          <div className="d-lg-flex d-none flex-lg-row mt-2  align-items-start ">
            <div className="d-flex align-items-center gap-2">
              {headerData?.header_fast_data && (
                <DevicesStatusChart
                  mctCount={headerData.header_fast_data.mct_count}
                  mctActive={headerData.header_fast_data.mct_active}
                  sbsCount={headerData.header_fast_data.srbm_count}
                  sbsActive={headerData.header_fast_data.srbm_active}
                  srstCount={headerData.header_fast_data.srst_count}
                  srstActive={headerData.header_fast_data.srst_active}
                  smstCount={headerData.header_fast_data.smst_count}
                  smstActive={headerData.header_fast_data.smst_active}
                  isFullInfo={false}
                  isFirstInfo={false}
                />
              )}
            </div>
            <div
              className="d-flex flex-lg-column   align-items-start justify-content-center gap-2 mt-2 mx-2 pt-1"
              style={{ width: '130px' }}
            >
              {headerData?.header_fast_data && headerData.header_fast_data.srst_count > 0 && (
                <span className="badge rounded-3 bg-success  mt-1">
                  Rats captured: {headerData.header_fast_data.srst_rats_caught}
                </span>
              )}
              {headerData?.header_fast_data && headerData.header_fast_data.smst_count > 0 && (
                <span className="badge rounded-3 bg-success  mt-1">
                  Mice captured: {headerData.header_fast_data.smst_mice_caught}
                </span>
              )}
            </div>
          </div>
          <div className="page-title-right">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <a href="/r/client-dashboard">
                    <span className="fw-bold">{t('ObjectNav.objectNav.myClients')}</span>
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <a href="/r/client-dashboard">
                    <span className="fw-bold">{headerData?.serviceprovider_name}</span>
                  </a>
                </li>
                <li className="breadcrumb-item">{headerData?.name}</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="page-title-box  d-flex align-items-center justify-content-between">
          <Box sx={{ width: '100%', height: '100%' }} className="mb-0 mb-sm-0">
            {isNotTablet ? (
              <div className="d-flex flex-column align-items-center justify-content-between">
                <Select
                  styles={{
                    control: provided => ({
                      ...provided,
                      backgroundColor: 'white',
                      color: 'black',
                      border: '2px solid #6a37d4',
                      padding: '0px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      boxShadow: 'none',
                      fontWeight: 'bold',
                      width: '200px',
                      minWidth: '180px',
                      margin: '0 auto',
                    }),
                    singleValue: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                    placeholder: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                    menu: provided => ({
                      ...provided,
                      backgroundColor: 'white',
                      zIndex: 9999,
                      outline: 'none',
                      border: '1px solid #6a37d4',
                      boxShadow: 'none',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#5f00cc' : state.isFocused ? '#d4b0f5' : 'white',
                      color: state.isSelected ? 'white' : state.isFocused ? 'white' : 'black',
                      cursor: 'pointer',
                    }),
                    dropdownIndicator: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                  }}
                  options={objectNavigationData}
                  placeholder="Go to.."
                  isSearchable
                  value={objectNavigationData.find(option => option.path === value)}
                  onChange={handleDropdownChange}
                  getOptionLabel={e => t(e.title as any)}
                  getOptionValue={e => e.path}
                />
                <div className="d-flex flex-row mt-4 ms-5 align-items-start">
                  <div className="d-flex align-items-center gap-1">
                    {headerData?.header_fast_data && (
                      <DevicesStatusChart
                        mctCount={headerData.header_fast_data.mct_count}
                        mctActive={headerData.header_fast_data.mct_active}
                        sbsCount={headerData.header_fast_data.srbm_count}
                        sbsActive={headerData.header_fast_data.srbm_active}
                        srstCount={headerData.header_fast_data.srst_count}
                        srstActive={headerData.header_fast_data.srst_active}
                        smstCount={headerData.header_fast_data.smst_count}
                        smstActive={headerData.header_fast_data.smst_active}
                        isFullInfo
                      />
                    )}
                  </div>
                  <div
                    className="d-flex flex-column align-items-start justify-content-center gap-2 mt-2 mx-2 pt-1"
                    style={{ width: '200px' }}
                  >
                    {headerData?.header_fast_data && headerData.header_fast_data.mct_count > 0 && (
                      <span className="badge rounded-3 bg-success mt-1">
                        Mice: {headerData.header_fast_data.mct_mice_caught}
                      </span>
                    )}
                    {headerData?.header_fast_data && headerData.header_fast_data.srbm_count > 0 && (
                      <span className="badge rounded-3 bg-success mt-1">
                        Bait consumed: {headerData.header_fast_data.bait_eaten}g
                      </span>
                    )}
                    {headerData?.header_fast_data && headerData.header_fast_data.srst_count > 0 && (
                      <span className="badge rounded-3 bg-success  mt-1">
                        Rats captured: {headerData.header_fast_data.srst_rats_caught}
                      </span>
                    )}
                    {headerData?.header_fast_data && headerData.header_fast_data.smst_count > 0 && (
                      <span className="badge rounded-3 bg-success  mt-1">
                        Mice captured: {headerData.header_fast_data.smst_mice_caught}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <CustomTabs
                value={isTabValid ? value : false}
                onChange={(event, newValue) => {
                  const selectedNavItem = objectNavigationData.find(item => item.path === newValue);
                  handleChange(event, newValue, selectedNavItem?.toDjango, selectedNavItem?.toPlan);
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobilearia-label="nav tabs example"
                allowScrollButtonsMobile
                role="navigation"
                sx={{ bgcolor: 'background.paper', maxWidth: 'fit-content', minHeight: '100%' }}
                selectionFollowsFocus
              >
                {objectNavigationData.map(item => (
                  <Tab
                    sx={{ height: '32px' }}
                    key={item.title}
                    title={item.tooltip ? t(item.tooltip as any) : ''}
                    label={
                      <div className="d-flex align-items-center">
                        <i className={`${item.icon} header-icon-style`} />
                        <div
                          className="d-flex align-items-center fs-14 bold"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          {t(item.title as any)}
                        </div>
                      </div>
                    }
                    value={item.path}
                  />
                ))}
              </CustomTabs>
            )}
          </Box>
        </div>
      </div>

      <Outlet />
    </div>
  );
};

export default ObjectNavPage;
